import React, { useState, useEffect } from 'react'
import { createStyles, withStyles } from '@mui/styles'
import { Paper, Tooltip, Typography, Dialog, DialogActions, DialogContent, DialogTitle, DialogContentText } from '@mui/material'
import { defaultStyles, muiOptions, MuiProps, defaultColors } from '../../infrastructure/materialUiThemeProvider'
import * as api from '../../infrastructure/api'
import { t } from '../../infrastructure/i18nextHelper'
import { Button, DataTable, ColumnDescriptor } from '../common/customComponents'
import guid, { Guid } from '../../infrastructure/guid'
import { ActionDescriptor } from '../common/components/table/table'
import { PlayCircleOutline, Check, Close, ErrorOutline } from '@mui/icons-material'

type BackgroundDiagnostic = {
    id: Guid
    name: string
    category: string
    lastStart: string | null
    lastEnd: string | null
    duration: string | null
    schedules: string
    lastStatus: string | null
    isSuccessful: boolean
}

type ColumnFilter = {
    name: string | null
    category: string | null
    lastStart: string | null
    lastEnd: string | null
    duration: string | null
    schedules: string | null
    lastStatus: string | null
}

let emptyFilter: ColumnFilter = {
    name: null,
    category: null,
    lastStart: null,
    lastEnd: null,
    duration: null,
    schedules: null,
    lastStatus: null
}

function BackgroundDiagnostics({ classes }: MuiProps) {
    let [diagnostics, setDiagnostics] = useState<BackgroundDiagnostic[]>([])
    let [filter, setFilter] = useState<ColumnFilter>(emptyFilter)
    let [dialogOpen, setDialogOpen] = useState(false)
    let [selectedItemName, setSelectedItemName] = useState<string>('')
    let [errorMessage, setErrorMessage] = useState<string>('')

    let tBase = 'admin.backgroundDiagnostics.'

    useEffect(() => {
        fetchData()
    }, [])

    const fetchData = async () => {
        let items = await api.get<BackgroundDiagnostic[]>('infra/backgroundDiagnostics')
        items = items.map(item => ({ ...item, id: guid.createNew() }))
        setDiagnostics(items)
    }

    let columns: ColumnDescriptor<BackgroundDiagnostic>[] = [
        {
            name: t(tBase + 'name'),
            value: x => x.name,
            columnFilter: {
                value: filter.name ?? '',
                onChange: (value: string) => setFilter({ ...filter, name: value })
            }
        },
        {
            name: t(tBase + 'category'),
            value: x => x.category,
            columnFilter: {
                value: filter.category ?? '',
                onChange: (value: string) => setFilter({ ...filter, category: value })
            }
        },
        {
            name: t(tBase + 'lastStart'),
            value: x => x.lastStart,
            columnFilter: {
                value: filter.lastStart ?? '',
                onChange: (value: string) => setFilter({ ...filter, lastStart: value })
            }
        },
        {
            name: t(tBase + 'lastEnd'),
            value: x => x.lastEnd,
            columnFilter: {
                value: filter.lastEnd ?? '',
                onChange: (value: string) => setFilter({ ...filter, lastEnd: value })
            }
        },
        {
            name: t(tBase + 'duration'),
            value: x => x.duration,
            columnFilter: {
                value: filter.duration ?? '',
                onChange: (value: string) => setFilter({ ...filter, duration: value })
            }
        },
        {
            name: t(tBase + 'schedules'),
            value: x => x.schedules,
            columnFilter: {
                value: filter.schedules ?? '',
                onChange: (value: string) => setFilter({ ...filter, schedules: value })
            }
        },
        {
            name: t(tBase + 'lastStatus'),
            htmlFor: (item: BackgroundDiagnostic) =>
                (item.isSuccessful ? <Check className={classes.green} /> : <Close className={classes.red} />)
        }
    ]

    let applyFilters = (items: BackgroundDiagnostic[], filter: ColumnFilter): BackgroundDiagnostic[] => {
        if (filter.name)
            items = items.filter(item => item.name.toLowerCase().contains(filter.name!.toLowerCase()))
        if (filter.category)
            items = items.filter(item => item.category.toLowerCase().contains(filter.category!.toLowerCase()))
        if (filter.lastStart)
            items = items.filter(item => item.lastStart?.toLowerCase().contains(filter.lastStart!.toLowerCase()))
        if (filter.lastEnd)
            items = items.filter(item => item.lastEnd?.toLowerCase().contains(filter.lastEnd!.toLowerCase()))
        if (filter.duration)
            items = items.filter(item => item.duration?.toLowerCase().contains(filter.duration!.toLowerCase()))
        if (filter.schedules)
            items = items.filter(item => item.schedules.toLowerCase().contains(filter.schedules!.toLowerCase()))

        return items
    }

    let getItems = (): BackgroundDiagnostic[] => applyFilters(diagnostics, filter)

    let run = async (item: BackgroundDiagnostic) => {
        let report = await api.get<{ errors: string }>('integrityChecks?name=' + item.name)
        setSelectedItemName(item.name)
        setErrorMessage(report.errors)
        setDialogOpen(true)
        await fetchData()
    }

    let openErrorDialog = (item: BackgroundDiagnostic) => {
        setSelectedItemName(item.name)
        setErrorMessage(item.lastStatus ?? '')
        setDialogOpen(true)
    }

    let actions: ActionDescriptor<BackgroundDiagnostic>[] = [
        {
            name: 'RunIntegrityCheck',
            action: run,
            icon: (
                <Tooltip title={<Typography variant='body1'>{t(tBase + 'run')}</Typography>} placement='top'>
                    <PlayCircleOutline />
                </Tooltip>
            ),
            isBodyAction: true,
            disabled: (item: BackgroundDiagnostic) => item.category !== 'Check',
        },
        {
            name: 'ShowError',
            action: openErrorDialog,
            icon: (
                <Tooltip title={<Typography variant='body1'>{t(tBase + 'showErrors')}</Typography>} placement='top'>
                    <ErrorOutline />
                </Tooltip>),
            isBodyAction: true,
            disabled: (item: BackgroundDiagnostic) => !item.lastStatus,
        }
    ]

    return (
        <div className={classes.container}>
            <Paper className={classes.paper}>
                <Typography className={classes.paperTitle} variant='overline' display='block' gutterBottom>
                    {t(tBase + 'title')}
                </Typography>
                <div>
                    <DataTable
                        columns={columns}
                        items={getItems()}
                        idSelector={(x: BackgroundDiagnostic) => x.id}
                        actions={actions} />
                </div>
            </Paper>

            <Dialog className={classes.dialogContainer}
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}>
                <DialogTitle >{t(tBase + 'lastStatusOf')} {selectedItemName}</DialogTitle>
                <DialogContent dividers={true} >
                    <DialogContentText >
                        {errorMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        label={t(tBase + 'close')}
                        onClick={() => setDialogOpen(false)}
                        color='primary' />
                </DialogActions>
            </Dialog>
        </div>
    )
}

let styles = theme =>
    createStyles({
        container: {
            ...defaultStyles.flexColumn,
            marginTop: '0.5em'
        },
        paper: {
            width: '100%',
            padding: '1em'
        },
        paperTitle: {
            color: defaultColors.red.main.color,
            marginLeft: "1em"
        },
        red: { color: defaultColors.red.main.color },
        green: { color: defaultColors.green.main.color },
        dialogContainer: {
            maxWidth: '60%',
            margin: '0 auto'
        }
    })

export default withStyles(styles, muiOptions)(BackgroundDiagnostics)